import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { format, parseISO } from 'date-fns';

const CATEGORY_COLORS: Record<string, string> = {
  news: 'var(--blue)',
  tech: '#059669',
  ai: '#7C3AED',
  innovation: '#D97706',
  opinion: '#DC2626',
};

interface BlogCardProps {
  post: any;
  variant?: 'large' | 'medium' | 'compact';
}

const BlogCard = ({ post, variant = 'medium' }: BlogCardProps) => {
  const { language } = useTranslation();
  const title = language === 'ro' ? (post.title_ro || post.title_en) : post.title_en;
  const excerpt = language === 'ro' ? (post.excerpt_ro || post.excerpt_en) : post.excerpt_en;
  const category = post.category || 'news';

  if (variant === 'compact') {
    return (
      <Link to={`/blog/${post.slug}`} className="flex gap-3 p-3 rounded-lg transition-colors hover:bg-[var(--off-white)] group">
        {post.cover_image && (
          <img src={post.cover_image} alt={title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <span className="text-[.6rem] font-bold uppercase tracking-widest" style={{ color: CATEGORY_COLORS[category], fontFamily: 'var(--f-mono)' }}>{category}</span>
          <h4 className="text-sm font-bold leading-tight line-clamp-2 group-hover:text-[var(--amber)] transition-colors" style={{ fontFamily: 'var(--f-display)', color: 'var(--ink)' }}>{title}</h4>
          <p className="text-[.7rem] text-[var(--slate-light)] mt-1">{post.reading_time_min || 3} min read</p>
        </div>
      </Link>
    );
  }

  if (variant === 'large') {
    return (
      <Link to={`/blog/${post.slug}`} className="block group">
        <div className="relative rounded-2xl overflow-hidden" style={{ border: '1px solid var(--line-slate)' }}>
          {post.cover_image ? (
            <img src={post.cover_image} alt={title} className="w-full aspect-[21/9] object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full aspect-[21/9]" style={{ background: 'var(--blue-light)' }} />
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,.7) 0%, transparent 60%)' }} />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <span className="inline-block px-3 py-1 rounded-full text-[.65rem] font-bold uppercase tracking-wider text-white mb-3" style={{ background: CATEGORY_COLORS[category], fontFamily: 'var(--f-mono)' }}>{category}</span>
            <h2 className="text-3xl font-bold text-white leading-tight mb-2" style={{ fontFamily: 'var(--f-display)' }}>{title}</h2>
            {excerpt && <p className="text-white/70 text-sm max-w-2xl line-clamp-2">{excerpt}</p>}
            <div className="flex gap-4 mt-3 text-white/50 text-xs" style={{ fontFamily: 'var(--f-mono)' }}>
              {post.published_at && <span>📅 {format(parseISO(post.published_at), 'MMM dd, yyyy')}</span>}
              <span>⏱ {post.reading_time_min || 3} min read</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="rounded-xl overflow-hidden transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-lg" style={{ border: '1px solid var(--line-slate)', background: 'var(--white)' }}>
        {post.cover_image ? (
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.cover_image} alt={title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" style={{ filter: 'brightness(.92)' }} />
          </div>
        ) : (
          <div className="aspect-[16/9]" style={{ background: 'var(--blue-light)' }} />
        )}
        <div className="p-5">
          <span className="inline-block px-2.5 py-0.5 rounded-full text-[.6rem] font-bold uppercase tracking-wider text-white mb-2" style={{ background: CATEGORY_COLORS[category], fontFamily: 'var(--f-mono)' }}>{category}</span>
          <h3 className="text-lg font-bold leading-tight mb-2 group-hover:text-[var(--amber)] transition-colors line-clamp-2" style={{ fontFamily: 'var(--f-display)', color: 'var(--blue)' }}>{title}</h3>
          {excerpt && <p className="text-sm line-clamp-2 mb-3" style={{ color: 'var(--slate)', lineHeight: 1.65 }}>{excerpt}</p>}
          <div className="flex gap-3 text-xs" style={{ color: 'var(--slate-light)', fontFamily: 'var(--f-mono)' }}>
            {post.published_at && <span>📅 {format(parseISO(post.published_at), 'MMM dd')}</span>}
            <span>⏱ {post.reading_time_min || 3} min</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
